// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportSnackbar } from '@/components/support-snackbar';
import { Project } from '@/project/api/types';
import { WorkflowSelector } from '@/project/components/workflow-selector';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { useWorkflows } from '@/workflow/hooks/use-workflows';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';

interface EnableWorkflowDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback to close the dialog
   */
  onClose: () => void;

  /**
   * The project to enable workflow for
   */
  project: Pick<Project, 'key' | 'name'>;

  /**
   * Query key to invalidate after successful update
   */
  queryKey?: string[];
}

const formSchema = z.object({
  workflowId: z.string().nullable(),
});

type FormData = z.infer<typeof formSchema>;

export function EnableWorkflowDialog(props: EnableWorkflowDialogProps) {
  const queryClient = useQueryClient();
  const { data: workflows = [] } = useWorkflows();
  const {
    mutateAsync,
    isPending,
    isError,
    reset: resetMutation,
  } = useUpdateProject(props.project.key, {
    onSuccess: () => {
      // Invalidate the projects list query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (props.queryKey) {
        queryClient.invalidateQueries({ queryKey: props.queryKey });
      }
    },
  });

  const { control, handleSubmit, reset, setValue } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workflowId: null,
    },
  });

  // Set default workflow when workflows are loaded and dialog is opened
  useEffect(() => {
    if (props.isOpen && workflows.length > 0) {
      const defaultWorkflow = workflows.find((workflow) => workflow.isDefault);
      if (defaultWorkflow) {
        setValue('workflowId', defaultWorkflow.id);
      }
    }
  }, [props.isOpen, workflows, setValue]);

  const workflowController = useController({
    control,
    name: 'workflowId',
  });

  const handleClose = () => {
    if (!isPending) {
      props.onClose();
      reset();
      resetMutation();
    }
  };

  const handleSubmit_ = async (data: FormData) => {
    try {
      await mutateAsync({
        workflowId: data.workflowId,
        workflowStageId: null, // Set stage to null - backend will assign first stage
      });
      handleClose();
    } catch (_error) {
      // Error handling is done by the mutation
    }
  };

  return (
    <>
      <Dialog
        open={props.isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleSubmit_),
        }}
      >
        <SupportSnackbar
          isOpen={isError}
          onClose={resetMutation}
          message="Failed to update project workflow"
        />

        <DialogTitle>Enable workflow</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              Select a workflow for <strong>{props.project.name}</strong> to
              start tracking progress
            </Typography>

            <WorkflowSelector
              value={workflowController.field.value}
              onChange={workflowController.field.onChange}
              disabled={isPending}
              label="Select workflow"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isPending}
            disabled={!workflowController.field.value}
          >
            Enable workflow
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
